import * as Yup from 'yup';
import { Divider, FormControlLabel, FormHelperText, Grid, Switch } from '@mui/material';
import { Field, FieldProps, Formik, FormikProps } from 'formik';
import { FormComponent } from '@synergy/abstract';
import { FormattedMessage, WrappedComponentProps, injectIntl } from 'react-intl';
import { Locale, Unit, UserRole } from '@synergy/models';
import {
    PoolButton,
    PoolFormikNumberTextfield,
    PoolFormikSelect,
    PoolFormikTextfield,
    PoolTypography,
} from '@synergy/components-next';
import { PoolMarkdownEditor, PoolModal } from '@synergy/components';
import { ReactNode } from 'react';
import { connect } from 'react-redux';
import { fields } from '@synergy/utils';
import { paloPrintedPosition } from '@synergy/components-palo';

import { AccessControl } from '../access-control/access-control';
import { AppState } from '../../../store/models/app-state';
import { CustomPosition } from './model/custom-position';
import { CustomPositionFields } from './model/custom-position-fields';
import { PaloPrintCount } from '../palo/palo-print-count';
import {
    PartialSupplier,
    PoolSupplierAutocompleteList,
} from '../autocomplete-list/supplier-autocomplete-list/pool-supplier-autocomplete-list';
import { Supplier } from '../../models/supplier';
import { fetchAllUnits } from '../../actions/unit-actions';

interface FreePositionDialogOwnProps<T> {
    locale: Locale;
    currencySymbol: string;
    customPosition?: CustomPosition;
    customData?: T;
    hideFields?: CustomPositionFields[];
    requiredFields?: CustomPositionFields[];
    title?: string;
    buttonText?: string;
    isOpen?: boolean;
    onSave?: (customProduct: CustomPosition, data?: T) => void;
    onCancel?: (data?: T) => void;
}

interface FreePositionDialogReduxProps {
    units: Unit[];
    fetchAllUnits: () => void;
    /** How many characters of the position text the PDF prints. */
    printCharacterLimit?: number;
}

type FreePositionDialogProps<T> = FreePositionDialogOwnProps<T> & FreePositionDialogReduxProps;

interface FormikValues extends Omit<CustomPosition, 'name' | 'longDescription' | 'shortDescription'> {
    name: string;
    longDescription: string;
    shortDescription: string;
}

const fieldNames = fields<FormikValues>();

class FreePositionDialogBase<T> extends FormComponent<
    FormikValues,
    FreePositionDialogProps<T> & WrappedComponentProps
> {
    private static defaultValues: FormikValues = {
        productNumber: '',
        isNormArticle: false,
        longDescription: '',
        shortDescription: '',
        name: '',
        price: 0,
        tax: 0,
        quantity: 1,
        orderUnit: '',
    };

    private customProductFormikInstance: FormikProps<FormikValues> | null = null;

    private handleSubmit = (values: FormikValues) => {
        if (this.customProductFormikInstance && this.customProductFormikInstance.isValid && this.props.onSave) {
            const { name, longDescription, shortDescription, ...formValues } = values;
            const customPosition: CustomPosition = {
                ...formValues,
                name: { de: '', en: '', de_ch: '', fr: '' },
                longDescription: { de: '', en: '', de_ch: '', fr: '' },
                shortDescription: { de: '', en: '', de_ch: '', fr: '' },
            };
            customPosition.name[this.props.locale] = name;
            if (customPosition.longDescription) {
                customPosition.longDescription[this.props.locale] = longDescription;
            }
            if (customPosition.shortDescription) {
                customPosition.shortDescription[this.props.locale] = shortDescription;
            }
            this.props.onSave(customPosition, this.props.customData);
        }
    };

    private handleSave = () => {
        if (this.customProductFormikInstance) {
            this.customProductFormikInstance.submitForm();
        }
    };

    private handleCancel = () => {
        if (this.props.onCancel) {
            this.props.onCancel(this.props.customData);
        }
    };

    private setCustomProductNormArticle(formikBag: FormikProps<FormikValues>, fieldName: string) {
        return () => {
            formikBag.setFieldValue(fieldName, !formikBag.values.isNormArticle);
        };
    }

    private setSupplierAccount(formikBag: FormikProps<FormikValues>) {
        return (supplier: Supplier | null) => {
            formikBag.setFieldValue('supplierAccountId', supplier && supplier.id);
            formikBag.setFieldValue('supplierAccountName', supplier && supplier.name);
        };
    }

    private setCustomProductFieldValue(formikBag: FormikProps<FormikValues>, fieldName: string) {
        return (value: string) => {
            formikBag.setFieldValue(fieldName, value);
        };
    }

    private getInitialValues(props: FreePositionDialogProps<T>): FormikValues {
        if (props.customPosition) {
            return {
                ...props.customPosition,
                name: props.customPosition.name[props.locale] ?? '',
                longDescription: props.customPosition.longDescription?.[props.locale] ?? '',
                shortDescription: props.customPosition.shortDescription?.[props.locale] ?? '',
            };
        }
        return FreePositionDialogBase.defaultValues;
    }

    public componentDidMount() {
        this.props.fetchAllUnits();
    }

    /**
     * How much of this position's text the print will carry.
     *
     * Printed is the short description where there is one and the long one
     * otherwise, so the count stands under whichever of the two that is — a
     * number under a text that is not printed would say nothing.
     */
    private printCount(values: FormikValues, underShort: boolean): ReactNode {
        const printed = paloPrintedPosition(values.shortDescription, values.longDescription);

        if (printed.isShort !== underShort || 0 === printed.length) {
            return null;
        }

        return <PaloPrintCount length={printed.length} limit={this.props.printCharacterLimit} />;
    }

    public render() {
        const actionChildren = [
            <PoolButton key="1" variant="text" onClick={this.handleCancel}>
                <FormattedMessage id="app.common.cancel" />
            </PoolButton>,
            <PoolButton id="save-free-article-position" key="2" onClick={this.handleSave}>
                <FormattedMessage
                    id={this.props.buttonText || this.props.customPosition ? 'app.common.save' : 'app.common.add'}
                />
            </PoolButton>,
        ];

        const selectableUnits = this.props.units.map(unit => {
            return {
                value: unit.translations[this.props.locale],
                displayValue: unit.translations[this.props.locale],
            };
        });

        const hideDescription =
            this.props.hideFields && this.props.hideFields.some(field => field === CustomPositionFields.Description);
        const hideNormArticle =
            this.props.hideFields && this.props.hideFields.some(field => field === CustomPositionFields.IsNormArticle);
        const hidePrice =
            this.props.hideFields && this.props.hideFields.some(field => field === CustomPositionFields.Price);
        const hideQuantity =
            this.props.hideFields && this.props.hideFields.some(field => field === CustomPositionFields.Quantity);
        const hideOrderUnit =
            this.props.hideFields && this.props.hideFields.some(field => field === CustomPositionFields.OrderUnit);
        const hideTax =
            this.props.hideFields && this.props.hideFields.some(field => field === CustomPositionFields.Tax);
        const hideContentQuantity =
            this.props.hideFields &&
            this.props.hideFields.some(field => field === CustomPositionFields.ContentQuantity);
        const hideContentUnit =
            this.props.hideFields && this.props.hideFields.some(field => field === CustomPositionFields.ContentUnit);

        const reqDescription =
            this.props.requiredFields &&
            this.props.requiredFields.some(field => field === CustomPositionFields.Description);
        const reqPrice =
            this.props.requiredFields && this.props.requiredFields.some(field => field === CustomPositionFields.Price);
        const reqQuantity =
            this.props.requiredFields &&
            this.props.requiredFields.some(field => field === CustomPositionFields.Quantity);
        const reqOrderUnit =
            this.props.requiredFields &&
            this.props.requiredFields.some(field => field === CustomPositionFields.OrderUnit);
        const reqTax =
            this.props.requiredFields && this.props.requiredFields.some(field => field === CustomPositionFields.Tax);
        const reqContentQuantity =
            this.props.requiredFields &&
            this.props.requiredFields.some(field => field === CustomPositionFields.ContentQuantity);
        const reqContentUnit =
            this.props.requiredFields &&
            this.props.requiredFields.some(field => field === CustomPositionFields.ContentUnit);

        const validationSchema = Yup.object().shape({
            name: Yup.string()
                .required('app.error.required')
                .max(255, v => this.props.intl.formatMessage({ id: 'app.error.maxLength' }, { value: v.max })),
            price: reqPrice ? Yup.number().required('app.error.required') : Yup.number(),
            tax: reqTax ? Yup.number().required('app.error.required') : Yup.number(),
            quantity: reqQuantity ? Yup.number().required('app.error.required') : Yup.number(),
            orderUnit: reqOrderUnit ? Yup.string().required('app.error.required') : Yup.string(),
            longDescription: reqDescription ? Yup.string().required('app.error.required') : Yup.string(),
            shortDescription: reqDescription ? Yup.string().required('app.error.required') : Yup.string(),
            contentQuantity: reqContentQuantity
                ? Yup.number().required('app.error.required').min(0, 'app.error.greaterZero')
                : Yup.number(),
            contentUnit: reqContentUnit ? Yup.string().required('app.error.required') : Yup.string(),

            isNormArticle: Yup.boolean(),
            productNumber: Yup.string().when('isNormArticle', {
                is: (isNormArticle: boolean) => isNormArticle,
                then: Yup.string().required('app.error.required'),
            }),
            supplierAccountName: Yup.mixed().when(['productNumber', 'isNormArticle'], {
                is: (productNumber: string, isNormArticle: boolean) => isNormArticle && productNumber,
                then: Yup.mixed().required('app.error.required'),
            }),
        });

        return (
            <PoolModal
                isVisible={this.props.isOpen || false}
                title={
                    <FormattedMessage
                        id={
                            this.props.title || this.props.customPosition
                                ? 'app.freePosition.editFreePosition'
                                : 'app.freePosition.addFreePosition'
                        }
                    />
                }
                handleClose={this.handleCancel}
                actionChildren={actionChildren}
                actionChildrenCentered={false}
                width="lg"
            >
                <Formik
                    innerRef={ref => (this.customProductFormikInstance = ref)}
                    initialValues={this.getInitialValues(this.props)}
                    onSubmit={this.handleSubmit}
                    validationSchema={validationSchema}
                    isInitialValid={true}
                >
                    {formikProps => (
                        <Grid container={true} spacing={2}>
                            <Grid container={true} item={true} spacing={2}>
                                <Grid item={true} xs={true}>
                                    <PoolFormikTextfield
                                        name={fieldNames.name}
                                        label={<FormattedMessage id="app.freePosition.name" />}
                                        required={true}
                                        fullWidth={true}
                                        hint={
                                            <FormattedMessage
                                                id="app.common.characterLimit"
                                                values={{ value: formikProps.values.name.length ?? 0, total: 55 }}
                                            />
                                        }
                                    />
                                </Grid>
                            </Grid>
                            <Grid container={true} spacing={2} item={true}>
                                <Grid item={true} xs={true}>
                                    <PoolFormikTextfield
                                        name={fieldNames.productNumber}
                                        label={<FormattedMessage id="app.freePosition.productNumber" />}
                                        required={formikProps.values.isNormArticle}
                                        fullWidth={true}
                                    />
                                </Grid>
                                {!hideTax && (
                                    <Grid item={true} xs={true}>
                                        <PoolFormikNumberTextfield
                                            minDecimalScale={2}
                                            name={fieldNames.tax}
                                            id="free-article-tax"
                                            label={<FormattedMessage id="app.freePosition.tax" />}
                                            required={reqTax}
                                            fullWidth={true}
                                        />
                                    </Grid>
                                )}
                                {!hidePrice && (
                                    <Grid item={true} xs={true}>
                                        <PoolFormikNumberTextfield
                                            name={fieldNames.price}
                                            id="free-article-price"
                                            label={
                                                <FormattedMessage
                                                    id="app.freePosition.price"
                                                    values={{
                                                        value: this.props.currencySymbol,
                                                    }}
                                                />
                                            }
                                            required={reqPrice}
                                            fullWidth={true}
                                        />
                                    </Grid>
                                )}
                            </Grid>
                            <Grid container={true} spacing={2} item={true} sx={{ mb: 3 }}>
                                {!hideOrderUnit && (
                                    <Grid item={true} xs={true}>
                                        <PoolFormikSelect
                                            name={fieldNames.orderUnit}
                                            fullWidth={true}
                                            options={selectableUnits}
                                            label={<FormattedMessage id={'app.freePosition.orderUnit'} />}
                                            required={reqOrderUnit}
                                        />
                                    </Grid>
                                )}
                                {!hideQuantity && (
                                    <Grid item={true} xs={true}>
                                        <PoolFormikNumberTextfield
                                            name={fieldNames.quantity}
                                            id="free-article-quantity"
                                            label={<FormattedMessage id="app.freePosition.quantity" />}
                                            required={reqQuantity}
                                            noNegativeNumbers={true}
                                            fullWidth={true}
                                        />
                                    </Grid>
                                )}
                                {!hideContentUnit && (
                                    <Grid item={true} xs={true}>
                                        <PoolFormikSelect
                                            name={fieldNames.contentUnit}
                                            fullWidth={true}
                                            options={selectableUnits}
                                            label={<FormattedMessage id={'app.freePosition.contentUnit'} />}
                                            required={reqContentUnit}
                                        />
                                    </Grid>
                                )}
                                {!hideContentQuantity && (
                                    <Grid item={true} xs={true}>
                                        <PoolFormikNumberTextfield
                                            name={fieldNames.contentQuantity}
                                            label={<FormattedMessage id="app.freePosition.contentQuantity" />}
                                            required={reqContentQuantity}
                                            noNegativeNumbers={true}
                                            fullWidth={true}
                                        />
                                    </Grid>
                                )}
                            </Grid>
                            {!hideDescription && (
                                <>
                                    <Grid container={true} item={true}>
                                        <Grid item={true} xs={true}>
                                            <PoolTypography
                                                typography="h6"
                                                translationId="app.freePosition.descriptionTitle"
                                                sx={{ mb: 1 }}
                                            />
                                            <Divider sx={{ backgroundColor: theme => theme.palette.text.primary }} />
                                        </Grid>
                                    </Grid>

                                    <Grid container={true} item={true}>
                                        <Grid item={true} xs={true}>
                                            <Field name={fieldNames.longDescription}>
                                                {({ field, form }: FieldProps<string>) => (
                                                    <PoolMarkdownEditor
                                                        {...field}
                                                        onChange={value => form.setFieldValue(field.name, value)}
                                                    />
                                                )}
                                            </Field>
                                            {this.printCount(formikProps.values, false)}
                                        </Grid>
                                    </Grid>
                                    <Grid container={true} item={true} sx={{ mb: '32px' }}>
                                        <Grid item={true} xs={true}>
                                            <PoolFormikTextfield
                                                name={fieldNames.shortDescription}
                                                label={<FormattedMessage id="app.freePosition.shortDescription" />}
                                                required={reqDescription}
                                                minRows="5"
                                                maxRows="50"
                                                multiline={true}
                                                fullWidth={true}
                                            />
                                            {this.printCount(formikProps.values, true)}
                                        </Grid>
                                    </Grid>
                                </>
                            )}
                            {!hideNormArticle && (
                                <AccessControl allowedRoles={[UserRole.Admin, UserRole.SuperAdmin]}>
                                    <Grid container={true} item={true}>
                                        <Grid item={true} xs={true}>
                                            <Field name={fieldNames.isNormArticle}>
                                                {({ field, form }: FieldProps<boolean>) => (
                                                    <div>
                                                        <FormControlLabel
                                                            control={
                                                                <Switch
                                                                    name="isNormArticle"
                                                                    color="primary"
                                                                    checked={field.value}
                                                                    disabled={this.props.customPosition?.isNormArticle}
                                                                    onChange={this.setCustomProductNormArticle(
                                                                        form,
                                                                        field.name
                                                                    )}
                                                                />
                                                            }
                                                            label={
                                                                <FormattedMessage id="app.freePosition.isNormArticle" />
                                                            }
                                                            // FIXME: should be done in the theme or as a variant of the component
                                                            sx={{
                                                                '& .MuiTypography-root': {
                                                                    fontSize: 18,
                                                                    fontWeight: 600,
                                                                    lineHeight: '130%',
                                                                },
                                                            }}
                                                        />
                                                        {this.hasError(form, field.name) && (
                                                            <FormHelperText error={true}>
                                                                {this.getError(form, field.name)}
                                                            </FormHelperText>
                                                        )}
                                                    </div>
                                                )}
                                            </Field>
                                            <Divider sx={{ backgroundColor: theme => theme.palette.text.primary }} />
                                        </Grid>
                                    </Grid>

                                    {formikProps.values.isNormArticle && (
                                        <Grid container={true} item={true}>
                                            <Grid item={true} xs={true}>
                                                <Field name="supplierAccount">
                                                    {({ field, form }: FieldProps<PartialSupplier>) => (
                                                        <div>
                                                            <PoolSupplierAutocompleteList
                                                                label={
                                                                    <FormattedMessage id="app.freePosition.supplierAccount" />
                                                                }
                                                                onSelected={this.setSupplierAccount(form)}
                                                                initialValue={formikProps.values.supplierAccountName}
                                                                error={this.hasError(form, 'supplierAccountName')}
                                                                disabled={this.props.customPosition?.isNormArticle}
                                                                required={!!form.values?.productNumber}
                                                                isLocked={false}
                                                                disablePreventOverflow={true}
                                                                {...field}
                                                            />
                                                            {this.hasError(form, 'supplierAccountName') && (
                                                                <FormHelperText error={true}>
                                                                    {this.getError(form, 'supplierAccountName')}
                                                                </FormHelperText>
                                                            )}
                                                        </div>
                                                    )}
                                                </Field>
                                            </Grid>
                                        </Grid>
                                    )}
                                    {!formikProps.values.isNormArticle && (
                                        <Grid container={true} item={true}>
                                            <Grid item={true} xs={true}>
                                                <FormattedMessage id="app.freePosition.editFreePositionHint" />
                                            </Grid>
                                        </Grid>
                                    )}
                                </AccessControl>
                            )}
                        </Grid>
                    )}
                </Formik>
            </PoolModal>
        );
    }
}

const mapStateToProps = (state: AppState) => ({
    units: state.units.allUnits,
    // What the PDF prints of a position text. Read from the settings so the
    // number lives in one place (FRM-Z, #2225).
    printCharacterLimit: state.settings.printCharacterLimit,
});

export const FreePositionDialog = connect(mapStateToProps, { fetchAllUnits })(injectIntl(FreePositionDialogBase));
